import { useState } from 'react';
import { usePromptDialog } from '../../../hooks/usePromptDialog';
import type {
  IsoItemApplicability,
  OrganizationItemContextView,
  TaskWithProgressView,
} from './types';
import { patchTaskProgress, upsertOrganizationContext } from './isoImplementationApi';
import { isTaskDone } from './isoImplementationUtils';
import { showToast } from '../../../components/ui/toast.utils';

const TYPE_LABELS: Record<string, string> = {
  ob: 'Obligatorio',
  pol: 'Política',
  proc: 'Procedimiento',
  reg: 'Registro',
  inf: 'Informe/Acta',
};

const WORKFLOW_LABELS: Record<string, string> = {
  pendiente: '— Pendiente',
  en_proceso: 'En proceso',
  borrador: 'Borrador',
  en_revision: 'En revisión',
  completado: 'Aprobado ✓',
};

const WORKFLOW_OPTIONS = Object.keys(WORKFLOW_LABELS);

const RESPONSIBLE_LABELS: Record<string, string> = {
  microsyst: 'Microsyst',
  cliente: 'Cliente',
  interno: 'Interno',
};

const APPLICABILITY_LABELS: Record<IsoItemApplicability, string> = {
  sin_definir: 'Sin definir',
  aplica: 'Aplica',
  no_aplica: 'No aplica',
};

interface DeliverableRowProps {
  task: TaskWithProgressView;
  canEdit: boolean;
  isSuperadmin: boolean;
  onTaskUpdated: (task: TaskWithProgressView) => void;
  onContextUpdated: (taskId: string, context: OrganizationItemContextView) => void;
}

export function DeliverableRow({
  task,
  canEdit,
  isSuperadmin,
  onTaskUpdated,
  onContextUpdated,
}: DeliverableRowProps) {
  const { prompt } = usePromptDialog();
  const [saving, setSaving] = useState(false);
  const docType = task.docType ?? task.tags[0] ?? '';
  const ctx = task.organizationContext;
  const workflow = ctx?.workflowStatus ?? 'pendiente';

  const saveContext = async (payload: Parameters<typeof upsertOrganizationContext>[1]) => {
    setSaving(true);
    try {
      const updated = await upsertOrganizationContext(task.id, payload);
      onContextUpdated(task.id, updated);
      showToast('Entregable actualizado correctamente.', 'success');
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const nextStatus = isTaskDone(task.progress.status) ? 'pending' : 'completed';
      const updated = await patchTaskProgress(task.id, { status: nextStatus });
      onTaskUpdated(updated);
      showToast(
        nextStatus === 'completed'
          ? 'Entregable marcado como completado.'
          : 'Se quitó la marca de completado.',
        'success',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={isTaskDone(task.progress.status) ? 'bg-muted/20' : undefined}>
      <td className="px-3 py-2 align-top">
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={() => void toggleComplete()}
          className={`flex h-5 w-5 items-center justify-center rounded border text-xs ${
            isTaskDone(task.progress.status)
              ? 'border-emerald-600 bg-emerald-600 text-white'
              : 'border-muted-foreground/40'
          }`}
          aria-label={`Marcar ${task.title}`}
        >
          {isTaskDone(task.progress.status) ? '✓' : ''}
        </button>
      </td>
      <td className="px-3 py-2 align-top min-w-[12rem]">
        <p className="font-medium">{task.title}</p>
        {task.detail ? <p className="mt-0.5 text-xs text-muted-foreground">{task.detail}</p> : null}
      </td>
      <td className="px-3 py-2 align-top text-xs whitespace-nowrap">
        {TYPE_LABELS[docType] ?? docType}
      </td>
      <td className="px-3 py-2 align-top font-mono text-xs">{task.clauseRef ?? '—'}</td>
      <td className="px-3 py-2 align-top">
        {canEdit ? (
          <select
            className="input text-xs w-full min-w-[7rem]"
            value={workflow}
            disabled={saving}
            onChange={(e) => void saveContext({ workflowStatus: e.target.value })}
          >
            {WORKFLOW_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {WORKFLOW_LABELS[value]}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs">{WORKFLOW_LABELS[workflow] ?? workflow}</span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        {canEdit ? (
          <select
            className="input text-xs w-full min-w-[6rem]"
            value={ctx?.responsibleParty ?? 'microsyst'}
            disabled={saving}
            onChange={(e) =>
              void saveContext({
                responsibleParty: e.target.value as OrganizationItemContextView['responsibleParty'],
              })
            }
          >
            {Object.entries(RESPONSIBLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs">
            {RESPONSIBLE_LABELS[ctx?.responsibleParty ?? ''] ?? '—'}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        {isSuperadmin ? (
          <select
            className="input text-xs w-full min-w-[6rem]"
            value={ctx?.applicability ?? 'sin_definir'}
            disabled={saving}
            onChange={(e) => {
              void (async () => {
                const next = e.target.value as IsoItemApplicability;
                if (next === 'no_aplica') {
                  const justification = await prompt({
                    title: 'No aplica',
                    message: 'Indique la justificación obligatoria.',
                    label: 'Justificación',
                    defaultValue: ctx?.applicabilityJustification ?? '',
                    confirmText: 'Guardar',
                    multiline: true,
                    required: true,
                  });
                  if (!justification) return;
                  await saveContext({
                    applicability: next,
                    applicabilityJustification: justification,
                  });
                  return;
                }
                await saveContext({ applicability: next, applicabilityJustification: null });
              })();
            }}
          >
            {(Object.keys(APPLICABILITY_LABELS) as IsoItemApplicability[]).map((value) => (
              <option key={value} value={value}>
                {APPLICABILITY_LABELS[value]}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs" title="Editable solo por superadmin">
            {APPLICABILITY_LABELS[ctx?.applicability ?? 'sin_definir']}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top min-w-[8rem]">
        {canEdit ? (
          <input
            type="text"
            className="input text-xs w-full"
            defaultValue={ctx?.evidenceNote ?? ''}
            disabled={saving}
            placeholder="Notas…"
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== (ctx?.evidenceNote ?? '')) {
                void saveContext({ evidenceNote: value || null });
              }
            }}
          />
        ) : (
          <span className="text-xs text-muted-foreground">{ctx?.evidenceNote ?? '—'}</span>
        )}
      </td>
    </tr>
  );
}
